<template>
  <div
    class="public-site-layout"
    :style="organizationStyles"
  >
    <PublicSiteHeader
      :org-slug="orgSlug"
      :organization-name="organizationInfo.name"
      :organization-logo="organizationInfo.logo"
      :contact-email="organizationInfo.contactEmail"
      :contact-phone="organizationInfo.contactPhone"
    />

    <main class="public-site-layout__main">
      <!-- No <Transition> here on purpose: some public pages are multi-root
           fragments (e.g. PublicLakesPage renders a div + BottomSheet), which
           <Transition mode="out-in"> cannot animate — it silently failed to
           insert the incoming page, blanking every in-app navigation of the
           public mini-site (including the marketplace → lake details funnel). -->
      <router-view v-slot="{ Component, route: currentRoute }">
        <component
          :is="Component"
          :key="currentRoute.fullPath"
          :org-info="organizationInfo"
        />
      </router-view>
    </main>

    <PublicSiteFooter
      :org-slug="orgSlug"
      :organization-name="organizationInfo.name"
      :contact-email="organizationInfo.contactEmail"
      :contact-phone="organizationInfo.contactPhone"
      :address="organizationInfo.address"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * @component PublicSiteLayout
 * @description Main layout for the public mini-site with organization branding
 * Includes header, footer, and manages CSS custom properties for theming
 */
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import PublicSiteHeader from '@/components/public/PublicSiteHeader.vue'
import PublicSiteFooter from '@/components/public/PublicSiteFooter.vue'
import { publicConfigService } from '@/services/publicApiService'

// Import the public site design system
import '@/css/public-site-variables.css'

interface OrganizationInfo {
  name?: string
  logo?: string
  contactEmail?: string
  contactPhone?: string
  address?: string
  primaryColor?: string
}

const route = useRoute()

const orgSlug = computed(() =>
  typeof route.params.orgSlug === 'string' ? route.params.orgSlug : undefined,
)

// Organization info - could be fetched from API or passed via route meta
const organizationInfo = ref<OrganizationInfo>({
  name: undefined,
  logo: undefined,
  contactEmail: undefined,
  contactPhone: undefined,
  address: undefined,
  primaryColor: undefined
})

// Convert hex color to HSL components for CSS custom properties
function hexToHSL(hex: string): { h: number; s: number; l: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return null

  const r = parseInt(result[1], 16) / 255
  const g = parseInt(result[2], 16) / 255
  const b = parseInt(result[3], 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  }
}

// Generate CSS custom properties for organization branding
const organizationStyles = computed(() => {
  const styles: Record<string, string> = {}

  if (organizationInfo.value.primaryColor) {
    const hsl = hexToHSL(organizationInfo.value.primaryColor)
    if (hsl) {
      styles['--public-org-primary-h'] = `${hsl.h}`
      styles['--public-org-primary-s'] = `${hsl.s}%`
      styles['--public-org-primary-l'] = `${hsl.l}%`
    }
  }

  return styles
})

// Fetch organization info when slug changes
async function fetchOrganizationInfo() {
  if (!orgSlug.value) return

  const formattedName = orgSlug.value
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

  organizationInfo.value = {
    name: formattedName,
    logo: undefined,
    contactEmail: undefined,
    contactPhone: undefined,
    address: undefined,
    primaryColor: undefined
  }

  try {
    const config = await publicConfigService.getOrganizationConfig(orgSlug.value)
    organizationInfo.value = {
      name: config.name || formattedName,
      logo: config.theme?.logo_url || undefined,
      contactEmail: config.contact?.email || undefined,
      contactPhone: config.contact?.phone || undefined,
      address: undefined,
      primaryColor: config.theme?.primary_color || undefined
    }
  } catch (error) {
    console.error('Failed to fetch organization info:', error)
  }
}

watch(orgSlug, fetchOrganizationInfo, { immediate: true })

onMounted(() => {
  fetchOrganizationInfo()
})
</script>

<style>
/* Import the design system globally when this layout is used */
.public-site-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--public-bg-primary);
  font-family: var(--public-font-family);
}

.public-site-layout__main {
  flex: 1 0 auto;
  padding-top: var(--public-header-height);
}

/* Page transition */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity var(--public-transition-base);
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}
</style>
